import React, { useState, useEffect, useMemo } from 'react';
import Layout from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import { Plus, Pencil, Trash2, ChevronRight, ChevronDown, Network, RefreshCw, FolderTree } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import { controllingAPI } from '../services/api';

const NODE_TYPES = [
  { value: 'ROOT', label: 'ROOT (top of tree)' },
  { value: 'GROUP', label: 'GROUP (intermediate folder)' },
  { value: 'LEAF', label: 'LEAF (cost center)' },
];

const TYPE_BADGE = {
  ROOT: 'bg-orange-100 text-orange-700',
  GROUP: 'bg-orange-100 text-orange-700',
  LEAF: 'bg-emerald-100 text-emerald-700',
};

const emptyForm = {
  node_id: '',
  controlling_area_id: '',
  parent_node_id: '',
  name: '',
  node_type: 'GROUP',
  cost_center_id: '',
};

const CostCenterHierarchy = () => {
  const { canWrite } = useAuth();
  const writable = canWrite('cost_center_hierarchy');
  const backendUrl = process.env.REACT_APP_BACKEND_URL;
  const [nodes, setNodes] = useState([]);
  const [costCenters, setCostCenters] = useState([]);
  const [controllingAreas, setControllingAreas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterCA, setFilterCA] = useState('all');
  const [expanded, setExpanded] = useState({});
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [formData, setFormData] = useState(emptyForm);

  // eslint-disable-next-line
  useEffect(() => { load(); }, []);

  const load = async () => {
    setLoading(true);
    try {
      const [nodesRes, ccsRes, casRes] = await Promise.all([
        axios.get(`${backendUrl}/api/controlling/cost-center-hierarchy`),
        controllingAPI.getCostCenters(),
        controllingAPI.getControllingAreas(),
      ]);
      setNodes(nodesRes.data || []);
      setCostCenters(ccsRes.data || []);
      setControllingAreas(casRes.data || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to load hierarchy');
    } finally { setLoading(false); }
  };

  // Build tree structure
  const tree = useMemo(() => {
    const filtered = filterCA === 'all'
      ? nodes
      : nodes.filter((n) => n.controlling_area_id === filterCA);
    const byParent = {};
    filtered.forEach((n) => {
      const key = n.parent_node_id || '__ROOT__';
      if (!byParent[key]) byParent[key] = [];
      byParent[key].push(n);
    });
    Object.keys(byParent).forEach((k) => byParent[k].sort((a, b) => a.name.localeCompare(b.name)));
    return byParent;
  }, [nodes, filterCA]);

  const toggleExpand = (id) => setExpanded((e) => ({ ...e, [id]: !e[id] }));

  const openCreate = (parent = null) => {
    setFormData({
      ...emptyForm,
      controlling_area_id: parent?.controlling_area_id || (controllingAreas[0]?.controlling_area_id || ''),
      parent_node_id: parent?.node_id || '',
      node_type: parent ? 'GROUP' : 'ROOT',
    });
    setEditMode(false);
    setDialogOpen(true);
  };

  const openEdit = (node) => {
    setFormData({
      node_id: node.node_id,
      controlling_area_id: node.controlling_area_id,
      parent_node_id: node.parent_node_id || '',
      name: node.name,
      node_type: node.node_type,
      cost_center_id: node.cost_center_id || '',
    });
    setEditMode(true);
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!formData.node_id || !formData.name || !formData.controlling_area_id) {
      toast.error('Node ID, Name and Controlling Area are required'); return;
    }
    if (formData.node_type === 'LEAF' && !formData.cost_center_id) {
      toast.error('LEAF nodes must have a cost center assigned'); return;
    }
    try {
      const payload = {
        ...formData,
        parent_node_id: formData.parent_node_id || null,
        cost_center_id: formData.cost_center_id || null,
      };
      if (editMode) {
        const { node_id, controlling_area_id, node_type, ...rest } = payload;
        await axios.put(`${backendUrl}/api/controlling/cost-center-hierarchy/${node_id}`, rest);
        toast.success('Node updated');
      } else {
        await axios.post(`${backendUrl}/api/controlling/cost-center-hierarchy`, payload);
        toast.success('Node created');
      }
      setDialogOpen(false);
      // Auto-expand parent so user sees the new child
      if (payload.parent_node_id) {
        setExpanded((e) => ({ ...e, [payload.parent_node_id]: true }));
      }
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Save failed');
    }
  };

  const handleDelete = async (node) => {
    const childCount = (tree[node.node_id] || []).length;
    if (childCount > 0) {
      toast.error(`Cannot delete '${node.name}' — it has ${childCount} child node(s). Delete or move them first.`);
      return;
    }
    if (!window.confirm(`Delete node '${node.name}' (${node.node_id})?`)) return;
    try {
      await axios.delete(`${backendUrl}/api/controlling/cost-center-hierarchy/${node.node_id}`);
      toast.success('Node deleted');
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Delete failed');
    }
  };

  const expandAll = () => {
    const all = {};
    nodes.forEach((n) => { all[n.node_id] = true; });
    setExpanded(all);
  };
  const collapseAll = () => setExpanded({});

  const renderNode = (node, depth = 0) => {
    const children = tree[node.node_id] || [];
    const isExpanded = !!expanded[node.node_id];
    const cc = node.cost_center_id ? costCenters.find((c) => c.cost_center_id === node.cost_center_id) : null;
    return (
      <div key={node.node_id}>
        <div
          className="flex items-center gap-2 py-1.5 px-2 rounded hover:bg-orange-50 group"
          style={{ paddingLeft: `${depth * 24 + 8}px` }}
          data-testid={`hier-node-${node.node_id}`}
        >
          <button
            onClick={() => children.length && toggleExpand(node.node_id)}
            className={`w-5 h-5 flex items-center justify-center rounded hover:bg-gray-200 ${!children.length ? 'invisible' : ''}`}
            data-testid={`hier-toggle-${node.node_id}`}
          >
            {isExpanded ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
          </button>
          <span className={`text-xs px-2 py-0.5 rounded font-medium ${TYPE_BADGE[node.node_type]}`}>
            {node.node_type}
          </span>
          <span className="font-mono text-xs text-gray-500">{node.node_id}</span>
          <span className="font-medium">{node.name}</span>
          {cc && (
            <Badge variant="outline" className="ml-1 text-xs">
              CC: {cc.cost_center_id} - {cc.name}
            </Badge>
          )}
          <div className="ml-auto opacity-0 group-hover:opacity-100 transition-opacity flex gap-1">
            {node.node_type !== 'LEAF' && (
              writable && (<Button variant="ghost" size="sm" onClick={() => openCreate(node)} data-testid={`hier-add-child-${node.node_id}`}>
                <Plus className="w-4 h-4" />
              </Button>)
            )}
            {writable && (<Button variant="ghost" size="sm" onClick={() => openEdit(node)} data-testid={`hier-edit-${node.node_id}`}>
              <Pencil className="w-4 h-4" />
            </Button>)}
            {writable && (<Button variant="ghost" size="sm" onClick={() => handleDelete(node)} data-testid={`hier-delete-${node.node_id}`}>
              <Trash2 className="w-4 h-4 text-red-500" />
            </Button>)}
          </div>
        </div>
        {isExpanded && children.map((c) => renderNode(c, depth + 1))}
      </div>
    );
  };

  const roots = tree.__ROOT__ || [];

  // Possible parent options for the dialog
  const parentOptions = nodes.filter(
    (n) => n.node_type !== 'LEAF' && n.controlling_area_id === formData.controlling_area_id
            && n.node_id !== formData.node_id
  );

  return (
    <Layout>
      <div className="p-6 max-w-7xl mx-auto" data-testid="cc-hierarchy-page">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-3xl font-bold flex items-center gap-2">
              <FolderTree className="w-7 h-7 text-orange-600" />
              Cost Center Hierarchy
            </h1>
            <p className="text-gray-600 mt-1">
              Standard hierarchy of cost centers per controlling area (ROOT → GROUPs → LEAF cost centers).
            </p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" size="sm" onClick={expandAll} data-testid="hier-expand-all">Expand all</Button>
            <Button variant="outline" size="sm" onClick={collapseAll} data-testid="hier-collapse-all">Collapse all</Button>
            <Button variant="outline" onClick={load} data-testid="hier-refresh">
              <RefreshCw className="w-4 h-4 mr-1" />Refresh
            </Button>
            {writable && (<Button onClick={() => openCreate(null)} data-testid="hier-new-root">
              <Plus className="w-4 h-4 mr-1" />New Root Node
            </Button>)}
          </div>
        </div>

        <Card className="mb-4">
          <CardContent className="pt-4 grid grid-cols-3 gap-3">
            <div>
              <Label>Controlling Area</Label>
              <Select value={filterCA} onValueChange={setFilterCA}>
                <SelectTrigger data-testid="hier-filter-ca"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Controlling Areas</SelectItem>
                  {controllingAreas.map((ca) => (
                    <SelectItem key={ca.controlling_area_id} value={ca.controlling_area_id}>
                      {ca.controlling_area_id} - {ca.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>{nodes.length} Total Node(s) ({roots.length} root{roots.length !== 1 && 's'})</CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="text-center py-10 text-gray-500">Loading…</div>
            ) : roots.length === 0 ? (
              <div className="text-center py-10 text-gray-500">
                <Network className="w-10 h-10 mx-auto mb-3 text-gray-300" />
                No hierarchy yet. Click <strong>New Root Node</strong> to start building one.
              </div>
            ) : (
              <div className="font-medium">
                {roots.map((r) => renderNode(r))}
              </div>
            )}
          </CardContent>
        </Card>

        <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
          <DialogContent className="max-w-xl" data-testid="hier-dialog">
            <DialogHeader>
              <DialogTitle>{editMode ? 'Edit' : 'New'} Hierarchy Node</DialogTitle>
            </DialogHeader>
            <div className="grid grid-cols-2 gap-4 py-2">
              <div>
                <Label>Node ID *</Label>
                <Input
                  value={formData.node_id}
                  maxLength={10}
                  disabled={editMode}
                  onChange={(e) => setFormData({ ...formData, node_id: e.target.value.toUpperCase() })}
                  data-testid="hier-node-id-input"
                />
              </div>
              <div>
                <Label>Name *</Label>
                <Input
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  data-testid="hier-name-input"
                />
              </div>
              <div>
                <Label>Controlling Area *</Label>
                <Select
                  value={formData.controlling_area_id}
                  onValueChange={(v) => setFormData({ ...formData, controlling_area_id: v, parent_node_id: '' })}
                  disabled={editMode}
                >
                  <SelectTrigger data-testid="hier-ca-select"><SelectValue placeholder="Select…" /></SelectTrigger>
                  <SelectContent>
                    {controllingAreas.map((ca) => (
                      <SelectItem key={ca.controlling_area_id} value={ca.controlling_area_id}>
                        {ca.controlling_area_id} - {ca.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Node Type *</Label>
                <Select
                  value={formData.node_type}
                  onValueChange={(v) => setFormData({ ...formData, node_type: v })}
                  disabled={editMode}
                >
                  <SelectTrigger data-testid="hier-node-type-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {NODE_TYPES.map((n) => (
                      <SelectItem key={n.value} value={n.value}>{n.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              {formData.node_type !== 'ROOT' && (
                <div className="col-span-2">
                  <Label>Parent Node *</Label>
                  <Select
                    value={formData.parent_node_id || 'none'}
                    onValueChange={(v) => setFormData({ ...formData, parent_node_id: v === 'none' ? '' : v })}
                  >
                    <SelectTrigger data-testid="hier-parent-select"><SelectValue placeholder="Select parent…" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">— No parent —</SelectItem>
                      {parentOptions.map((p) => (
                        <SelectItem key={p.node_id} value={p.node_id}>
                          [{p.node_type}] {p.node_id} - {p.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
              {formData.node_type === 'LEAF' && (
                <div className="col-span-2">
                  <Label>Cost Center *</Label>
                  <Select
                    value={formData.cost_center_id || 'none'}
                    onValueChange={(v) => setFormData({ ...formData, cost_center_id: v === 'none' ? '' : v })}
                  >
                    <SelectTrigger data-testid="hier-cc-select"><SelectValue placeholder="Select cost center…" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">— None —</SelectItem>
                      {costCenters
                        .filter((c) => c.controlling_area_id === formData.controlling_area_id)
                        .map((c) => (
                          <SelectItem key={c.cost_center_id} value={c.cost_center_id}>
                            {c.cost_center_id} - {c.name}
                          </SelectItem>
                        ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
              <Button onClick={handleSave} data-testid="hier-save-btn">{editMode ? 'Update' : 'Create'}</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
};

export default CostCenterHierarchy;
